// KPIs › Scorecard › Published tab (`/c/{slug}/kpis/scorecard?view=published`; UX-A moved it from
// its own page, `/kpis/publications` redirects; `kpi`; coverage rows 54–56): the
// frozen scorecards. Managers publish revision 1 of a week for the company or one seat
// (`POST /scorecards/publish`; `already_published` says to revise instead) and append a
// revision with a reason (`POST …/publications/{id}/revise`, scored under the stored config).
// Everyone who reads the scorecard sees the history (`GET /scorecards/publications`, newest
// first) and opens any revision's frozen numbers in a drawer. In grace: read-only.
import {
  Badge,
  BandBadge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerSection,
  DrawerTitle,
  EmptyState,
  Field,
  FieldLabel,
  InfoTip,
  NativeSelect,
  NativeSelectOption,
  Segmented,
  StatGrid,
  StatTile,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
  Toolbar,
} from "@companyos/ui";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { BookCheckIcon, StampIcon } from "lucide-react";
import { type FormEvent, useState } from "react";
import type { Api } from "../../api/client";
import type { ScorecardPublication } from "../../api/types";
import { recentWeeks } from "../../focus/focus";
import { weekRangeLabel } from "../../focus/weeks";
import { type CompanyAccess, isManager } from "../company";
import {
  formatDateTime,
  formatHours,
  formatMetricValue,
  formatMoney,
  formatScore,
  formatTarget,
  STATE_LABELS,
} from "../format";
import { digitalEmployeesQuery, publicationsQuery, structureKeys } from "../queries";
import { coverageText, Loading } from "../screen-parts";
import { useEveryPage } from "../use-every-page";
import { useFeature } from "../use-feature";
import { ErrorNotice, FormError, MoreRows, problemCode, ReadOnlyNotice } from "./parts";

type Filter = "all" | "company" | "seat";

/** Revisions grouped by subject and week; the newest revision of each is the current one. */
function latestOf(rows: ScorecardPublication[]): Map<string, number> {
  const out = new Map<string, number>();
  for (const r of rows) {
    const k = `${r.subject_kind}|${r.subject_id}|${r.period_type}|${r.period_key}`;
    out.set(k, Math.max(out.get(k) ?? 0, r.revision));
  }
  return out;
}
const groupKey = (r: ScorecardPublication) =>
  `${r.subject_kind}|${r.subject_id}|${r.period_type}|${r.period_key}`;

export function PublicationsScreen({ api, company }: { api: Api; company: CompanyAccess }) {
  const kpi = useFeature("kpi");
  const manager = isManager(company);
  const canWrite = manager && !kpi.readOnly;
  const q = useInfiniteQuery(publicationsQuery(api, company.id));
  const [filter, setFilter] = useState<Filter>("all");
  const [publishing, setPublishing] = useState(false);
  const [revising, setRevising] = useState<ScorecardPublication | null>(null);
  const [open, setOpen] = useState<ScorecardPublication | null>(null);
  const rows = q.data?.pages.flatMap((p) => p.data) ?? [];
  const latest = latestOf(rows);
  const shown = rows.filter((r) => filter === "all" || r.subject_kind === filter);

  return (
    <>
      {manager && kpi.readOnly ? <ReadOnlyNotice what="published scorecards" /> : null}
      <Toolbar className="mb-4">
        <InfoTip label="About published scorecards">
          A published week is frozen: its numbers and the configuration they were scored with never
          change. A correction is a new revision with its reason.
        </InfoTip>
        <Segmented
          aria-label="Subject"
          size="sm"
          value={filter}
          onValueChange={setFilter}
          options={[
            { value: "all", label: "All" },
            { value: "company", label: "Company" },
            { value: "seat", label: "Seats" },
          ]}
        />
        <span className="ml-auto text-xs text-muted-foreground tabular-nums">
          {q.data ? `${shown.length} revision${shown.length === 1 ? "" : "s"}` : ""}
        </span>
        {canWrite ? (
          <Button size="sm" onClick={() => setPublishing(true)}>
            <StampIcon data-icon="inline-start" />
            Publish a week
          </Button>
        ) : null}
      </Toolbar>
      {q.isPending ? (
        <Loading label="Loading published scorecards" rows={4} />
      ) : q.isError ? (
        <ErrorNotice what="Published scorecards" error={q.error} />
      ) : !rows.length ? (
        <EmptyState
          variant="card"
          icon={BookCheckIcon}
          title="Nothing published yet"
          description={
            canWrite
              ? "Publish a finished week to freeze its scorecard. Generating a Friday report also publishes its week."
              : "Frozen scorecards appear here once a manager publishes a week."
          }
          actions={
            canWrite ? (
              <Button onClick={() => setPublishing(true)}>Publish a week</Button>
            ) : undefined
          }
        />
      ) : (
        <div className="flex min-w-0 flex-col">
          <Table data-slot="publications">
            <TableCaption>Every revision, newest first.</TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead scope="col">Week</TableHead>
                <TableHead scope="col">Subject</TableHead>
                <TableHead scope="col">Revision</TableHead>
                <TableHead scope="col">Band</TableHead>
                <TableHead scope="col">Published</TableHead>
                <TableHead scope="col">Reason</TableHead>
                <TableHead scope="col">
                  <span className="sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {shown.map((r) => {
                const current = latest.get(groupKey(r)) === r.revision;
                return (
                  <TableRow
                    key={r.id}
                    data-publication={r.id}
                    data-revision={r.revision}
                    data-state={open?.id === r.id ? "selected" : undefined}
                  >
                    <TableHead scope="row" className="tabular-nums">
                      {r.period_key}
                    </TableHead>
                    <TableCell>
                      {r.subject_kind === "company" ? "Company" : r.snapshot.subject.name}
                    </TableCell>
                    <TableCell>
                      <span className="flex items-center gap-1.5">
                        r{r.revision}
                        {current ? <Badge variant="accent">Current</Badge> : null}
                      </span>
                    </TableCell>
                    <TableCell>
                      <BandBadge band={r.snapshot.band} />
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDateTime(r.published_at)}
                    </TableCell>
                    <TableCell className="max-w-64 text-muted-foreground">
                      {r.reason ?? "—"}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-right">
                      <Button size="sm" variant="ghost" onClick={() => setOpen(r)}>
                        View
                        <span className="sr-only">
                          {" "}
                          r{r.revision} of {r.period_key}
                        </span>
                      </Button>
                      {canWrite && current ? (
                        <Button size="sm" variant="secondary" onClick={() => setRevising(r)}>
                          Revise
                          <span className="sr-only">
                            {" "}
                            {r.period_key}{" "}
                            {r.subject_kind === "company" ? "company" : r.snapshot.subject.name}
                          </span>
                        </Button>
                      ) : null}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <MoreRows what="revisions" q={q} />
        </div>
      )}
      {publishing ? (
        <PublishDialog api={api} company={company} onClose={() => setPublishing(false)} />
      ) : null}
      {revising ? (
        <ReviseDialog api={api} co={company.id} from={revising} onClose={() => setRevising(null)} />
      ) : null}
      {open ? <PublicationDrawer publication={open} onClose={() => setOpen(null)} /> : null}
    </>
  );
}

function PublishDialog({
  api,
  company,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const des = useInfiniteQuery(digitalEmployeesQuery(api, company.id));
  // WAVE12-FIX: every DE's seat can be published, not only the first page's 50.
  useEveryPage(des);
  const seats = (des.data?.pages.flatMap((p) => p.data) ?? []).flatMap((d) =>
    d.seat ? [{ id: d.seat.id, label: `${d.display_name} · ${d.seat.title}` }] : [],
  );
  // Weeks that have ended: last week first (the current one is still moving).
  const weeks = recentWeeks(new Date(), 9).slice(1);
  const [subject, setSubject] = useState("company");
  const [week, setWeek] = useState(weeks[0] ?? "");
  const publish = useMutation({
    mutationFn: () =>
      api.client.scorecards.publish({
        params: { co: company.id },
        body: {
          subject_kind: subject === "company" ? "company" : "seat",
          subject_id: subject === "company" ? company.id : subject,
          period_type: "week",
          period_key: week,
        },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: structureKeys.publications(company.id) });
      onClose();
    },
  });
  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    publish.mutate();
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <form onSubmit={onSubmit} className="flex flex-col gap-5" aria-labelledby="publish-title">
          <DialogHeader>
            <DialogTitle id="publish-title">Publish a week</DialogTitle>
            <DialogDescription>
              Freezes revision 1 of the week's scorecard with the metrics, targets and weights in
              force at the end of the week.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-5 sm:grid-cols-2">
            <Field>
              <FieldLabel htmlFor="publish-subject">Scorecard</FieldLabel>
              <NativeSelect
                id="publish-subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              >
                <NativeSelectOption value="company">{company.name} (company)</NativeSelectOption>
                {seats.map((s) => (
                  <NativeSelectOption key={s.id} value={s.id}>
                    {s.label}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
            </Field>
            <Field>
              <span className="flex items-center gap-1">
                <FieldLabel htmlFor="publish-week">Week</FieldLabel>
                <InfoTip label="About the week">Weeks that have ended.</InfoTip>
              </span>
              <NativeSelect
                id="publish-week"
                value={week}
                onChange={(e) => setWeek(e.target.value)}
              >
                {weeks.map((w) => (
                  <NativeSelectOption key={w} value={w}>
                    {w} · {weekRangeLabel(w)}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
            </Field>
          </div>
          {problemCode(publish.error) === "already_published" ? (
            <FormError
              what="Publishing"
              error={new Error("This week is already published: revise it from the list instead.")}
            />
          ) : (
            <FormError what="Publishing" error={publish.error} />
          )}
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" size="lg" disabled={publish.isPending || !week}>
              {publish.isPending ? "Publishing…" : "Publish"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ReviseDialog({
  api,
  co,
  from,
  onClose,
}: {
  api: Api;
  co: string;
  from: ScorecardPublication;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [reason, setReason] = useState("");
  const ok = reason.trim().length >= 10;
  const revise = useMutation({
    mutationFn: () =>
      api.client.scorecards.revise({
        params: { co, id: from.id },
        body: { reason: reason.trim() },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: structureKeys.publications(co) });
      onClose();
    },
  });
  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (ok) revise.mutate();
  };
  const who = from.subject_kind === "company" ? "the company" : from.snapshot.subject.name;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <form onSubmit={onSubmit} className="flex flex-col gap-5" aria-labelledby="revise-title">
          <DialogHeader>
            <DialogTitle id="revise-title">
              Revise {from.period_key} for {who}
            </DialogTitle>
            <DialogDescription>
              Appends revision {from.revision + 1}, re-scored from today's data under the
              configuration frozen in revision 1. Earlier revisions stay readable.
            </DialogDescription>
          </DialogHeader>
          <Field data-invalid={reason && !ok ? true : undefined}>
            <span className="flex items-center gap-1">
              <FieldLabel htmlFor="revise-reason">Reason</FieldLabel>
              <InfoTip label="About the reason">
                At least 10 characters; it is shown with the revision.
              </InfoTip>
            </span>
            <Textarea
              id="revise-reason"
              rows={3}
              required
              maxLength={2000}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </Field>
          <FormError what="Revising" error={revise.error} />
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" size="lg" disabled={!ok || revise.isPending}>
              {revise.isPending ? "Revising…" : `Publish revision ${from.revision + 1}`}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function PublicationDrawer({
  publication: p,
  onClose,
}: {
  publication: ScorecardPublication;
  onClose: () => void;
}) {
  const card = p.snapshot;
  const currency = card.cost?.currency ?? "USD";
  return (
    <Drawer open onOpenChange={(o) => !o && onClose()}>
      <DrawerHeader closeLabel="Close the published scorecard">
        Scorecard › Published › {p.period_key}
      </DrawerHeader>
      <DrawerBody data-slot="publication-drawer">
        <DrawerTitle>
          {p.subject_kind === "company" ? "Company" : card.subject.name} · {p.period_key} · r
          {p.revision}
        </DrawerTitle>
        <p className="mt-2 text-sm text-muted-foreground">
          Published {formatDateTime(p.published_at)} · {weekRangeLabel(p.period_key)}
          {p.reason ? ` · ${p.reason}` : ""}
        </p>
        <StatGrid className="mt-5 mb-0">
          <StatTile
            label="KPI score"
            value={formatScore(card.kpi_score)}
            band={card.band}
            detail={coverageText(card.completeness, card.band)}
          />
          <StatTile label="Goal score" value={formatScore(card.goal_score)} />
          <StatTile
            label="Cost (week)"
            value={card.cost ? formatMoney(card.cost.total_minor, currency) : "—"}
          />
          <StatTile
            label="Supervisor hours"
            value={card.hours ? formatHours(card.hours.supervision_minutes) : "—"}
          />
        </StatGrid>
        <DrawerSection title={card.children?.length ? "Digital employees" : "Metrics"}>
          {card.children?.length ? (
            <Table>
              <TableCaption>The DE seats this company scorecard rolled up, frozen.</TableCaption>
              <TableHeader>
                <TableRow>
                  <TableHead scope="col">Seat</TableHead>
                  <TableHead scope="col" numeric>
                    KPI score
                  </TableHead>
                  <TableHead scope="col">Band</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {card.children.map((c) => (
                  <TableRow key={c.subject.id}>
                    <TableHead scope="row">{c.subject.name}</TableHead>
                    <TableCell numeric>{formatScore(c.kpi_score)}</TableCell>
                    <TableCell>
                      <BandBadge band={c.band} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : card.metrics.length ? (
            <Table>
              <TableCaption>Actual against target per metric, as published.</TableCaption>
              <TableHeader>
                <TableRow>
                  <TableHead scope="col">Metric</TableHead>
                  <TableHead scope="col" numeric>
                    Actual
                  </TableHead>
                  <TableHead scope="col" numeric>
                    Target
                  </TableHead>
                  <TableHead scope="col">State</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {card.metrics.map((m) => (
                  <TableRow key={m.metric_def_id}>
                    <TableHead scope="row">{m.name}</TableHead>
                    <TableCell numeric>{formatMetricValue(m.actual, m.unit, currency)}</TableCell>
                    <TableCell numeric>{formatTarget(m, currency)}</TableCell>
                    <TableCell className="text-muted-foreground">{STATE_LABELS[m.state]}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="text-sm text-muted-foreground">No metrics were on this scorecard.</p>
          )}
        </DrawerSection>
      </DrawerBody>
    </Drawer>
  );
}
